import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Heart, Ticket } from "lucide-react";
import { IMAGES } from "@/config/content";
import { useDonate } from "@/context/DonateContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/legacy", label: "His Legacy" },
  { to: "/ethiopia", label: "Our Work in Ethiopia" },
  { to: "/board", label: "Board Members" },
  { to: "/gala", label: "Gala" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openDonate } = useDonate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,padding,border-color] duration-500 border-b ${
          scrolled
            ? "bg-ink/90 backdrop-blur-xl border-gold/20 py-2"
            : "bg-ink/40 backdrop-blur-md border-transparent py-4"
        }`}
        data-testid="navbar"
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo" aria-label="EHSIMFFA home">
            <img
              src={IMAGES.logo}
              alt="Emperor Haile Selassie I Memorial Foundation seal"
              className={`transition-all duration-500 ${scrolled ? "h-10 w-10" : "h-12 w-12"}`}
            />
            <span className="hidden sm:flex flex-col leading-none">
              <span className="font-serif text-xl text-ivory">EHSIMFFA</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-gold/80">Friends in America</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-wide transition-colors hover:text-gold ${
                    isActive ? "text-gold" : "text-ivory2"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/gala"
              data-testid="nav-gala-btn"
              className="hidden xl:inline-flex items-center gap-2 rounded-sm border border-gold/50 px-4 py-2.5 text-sm font-bold uppercase tracking-wider text-gold transition-colors hover:bg-gold/10"
            >
              <Ticket className="h-4 w-4" /> Gala 2026
            </Link>
            <button
              onClick={() => openDonate()}
              data-testid="nav-donate-btn"
              className="hidden sm:inline-flex items-center gap-2 rounded-sm bg-gold px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-ink transition-colors hover:bg-goldhi"
            >
              <Heart className="h-4 w-4" /> Donate
            </button>
            <button
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-sm text-ivory hairline"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="nav-mobile-toggle"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[82%] max-w-sm bg-ink2 border-l border-gold/20 px-6 pt-24 pb-8 overflow-y-auto">
            <div className="flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `border-b border-gold/10 py-4 text-lg ${isActive ? "text-gold" : "text-ivory"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
            <Link
              to="/gala"
              onClick={() => setOpen(false)}
              data-testid="mobile-gala-btn"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-sm border border-gold/50 py-4 text-base font-bold uppercase tracking-wider text-gold"
            >
              <Ticket className="h-5 w-5" /> Gala Tickets 2026
            </Link>
            <button
              onClick={() => { setOpen(false); openDonate(); }}
              data-testid="mobile-donate-btn"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-sm bg-gold py-4 text-base font-bold uppercase tracking-wider text-ink"
            >
              <Heart className="h-5 w-5" /> Donate Now
            </button>
          </div>
        </div>
      )}

      {/* Persistent mobile donate bar */}
      <button
        onClick={() => openDonate()}
        data-testid="sticky-donate-btn"
        className="sm:hidden fixed bottom-0 inset-x-0 z-30 flex items-center justify-center gap-2 bg-gold py-4 text-base font-bold uppercase tracking-wider text-ink shadow-[0_-6px_24px_rgba(0,0,0,0.5)]"
      >
        <Heart className="h-5 w-5" /> Donate to Support a Student
      </button>
    </>
  );
}
